import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { textSecondary, primaryAlt, primary } from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { getCaseAcceptanceStatistics } from "../../../../actions/statisticActions";
import { FaCircleInfo } from "react-icons/fa6";
import useThemeColors from "../../../../hooks/useThemeColors";
import useCountUpTrigger from "../../../../hooks/useCountUpTrigger";
import { useNavigate, useParams } from "react-router-dom";
// import GaugeChart from "react-gauge-chart";

const DashboardCaseAcceptance = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { whiteMode, textPrimaryMode } = useThemeColors();

  // STATE
  const [isCountUpReady, setIsCountUpReady] = useState(false);

  // REDUX
  const dispatch = useDispatch();

  const statisticCaseAcceptance = useSelector(
    (state) => state.statisticCaseAcceptance
  );
  const { loading, error, caseAcceptance } = statisticCaseAcceptance;

  // FETCH DATA
  // useEffect(() => {
  //   console.log("Slug changed to:", slug); // Check if slug is updating
  //   if (!caseAcceptance || Object.keys(caseAcceptance).length === 0) {
  //     // Fetch data only if caseAcceptance is undefined or an empty object
  //     dispatch(getCaseAcceptanceStatistics(slug));
  //   } else {
  //     // Set the flag to start the animation
  //     setIsCountUpReady(true);
  //   }
  // }, [dispatch, caseAcceptance, slug]);

  useEffect(() => {
    // console.log("Slug changed to:", slug); // Check if slug is updating

    dispatch(getCaseAcceptanceStatistics(slug));
  }, [slug]);

  useEffect(() => {
    if (caseAcceptance && Object.keys(caseAcceptance).length > 0) {
      setIsCountUpReady(true); // Trigger animation once data is available
    }
  }, [caseAcceptance]);

  // CUSTOM
  // Process the percentage as soon as we receive it
  const caseAcceptancePercentage = caseAcceptance
    ? Math.floor(parseFloat(caseAcceptance.caseAcceptancePercentage) || 0)
    : 0;

  const remainingPercentage = 100 - caseAcceptancePercentage;

  const animatedCaseAcceptancePercentage = useCountUpTrigger(
    caseAcceptancePercentage,
    isCountUpReady
  );

  // LOGS
  // console.log("slug:", slug);
  // console.log("caseAcceptance:", caseAcceptance);

  // LOADING
  if (loading)
    return (
      <Flex
        w="100%"
        bg={whiteMode}
        p="1rem 1.5rem"
        borderRadius="1rem"
        // h="172px"
        // minH="172px"
        // maxH="172px"
        h="146px"
        minH="146px"
        maxH="146px"
        align="center"
        justify="center"
      >
        <Spinner size="lg" color={primary} />
      </Flex>
    );
  if (error) return <div>Error loading data</div>;

  return (
    <Flex
      key={slug}
      w="100%"
      bg={whiteMode}
      borderRadius="1rem"
      //   p="1rem 1.5rem"
      p="1rem 1.5rem"
      h="146px"
      minH="146px"
      maxH="146px"
      direction="column"
    >
      <Flex w="100%" align="center" justify="space-between" minH="2rem">
        <Flex align="center">
          <Heading
            fontSize="1.125rem"
            fontWeight="600"
            color={textPrimaryMode}
            mr="0.25rem"
          >
            {t("dashboard.caseAcceptance")}
          </Heading>
          <Tooltip
            p="0.5rem 1rem"
            borderRadius="1rem"
            placement="top"
            label={
              <Flex direction="column">
                <Text fontSize="xs" color={whiteMode} fontWeight="500">
                  {t("dashboard.caseAcceptanceDescription")}
                </Text>
              </Flex>
            }
          >
            <IconButton
              variant="ghost"
              size="xs"
              bg="none"
              color="none"
              _hover={{
                bg: "none",
                color: "none",
              }}
            >
              <Icon
                as={FaCircleInfo}
                fontSize="0.875rem"
                color={primaryAlt}
                position="relative"
                top="-1px"
              />
              {/* <Icon
                as={FaCircleQuestion}
                color={textSecondary}
                fontSize="0.875rem"
                position="relative"
              /> */}
            </IconButton>
          </Tooltip>
        </Flex>

        <Flex align="center" justify="flex-end" gap="0.5rem"></Flex>
      </Flex>

      <Box w="100%">
        <Flex
          w="100%"
          align="center"
          justify="center"
          gap="2rem"
          m="0.25rem 0 0"
        >
          <Flex
            // bg={backgroundLight}
            color={textPrimaryMode}
            borderRadius="1rem"
            p="0.5rem 1rem 0.25rem"
            m="0 0 0.25rem"
          >
            <Text fontSize="2.5rem" fontWeight="500" lineHeight="1">
              {animatedCaseAcceptancePercentage}%
            </Text>
          </Flex>
        </Flex>
      </Box>

      <Flex align="center" justify="center" gap="0.25rem" m="0rem 0 0">
        <Text fontSize="xs" color={textSecondary} fontWeight="500">
          {caseAcceptance.acceptedPlansCount || 0}/{" "}
          {caseAcceptance.proposedPlansCount || 0}{" "}
          {t("dashboard.caseAcceptancePlans")}
        </Text>
      </Flex>

      {/* <Box>
        <Flex direction="column">
          <Text fontSize="0.8rem" color={textSecondary} fontWeight="500">
            {caseAcceptance.acceptedPlansCount || 0}
            {t("dashboard.acceptedPlansCount")}
          </Text>

          <Text fontSize="0.8rem" color={textSecondary} fontWeight="500">
            {caseAcceptance.proposedPlansCount || 0}{" "}
            {t("dashboard.proposedPlansCount")}
          </Text>
        </Flex>
      </Box> */}

      {/* <Flex w="100%" align="flex-start" direction="column" p="0rem 0 0rem">
        <Flex w="100%" align="baseline" gap="0.5rem" m="0.5rem 0 0">
          <Text fontSize="2.5rem" fontWeight="500">
            {animatedCaseAcceptancePercentage}%
          </Text>
          <Text fontSize="xs" fontWeight="500" color={textSecondary}>
            {t("dashboard.caseAcceptancePercentageText")}
          </Text>
        </Flex>

        <Flex
          w="100%"
          m="0.5rem 0 0"
          h="0.5rem"
          align="center"
          justify="center"
          gap="0.25rem"
        >
          <Flex w="100%" borderRadius="1rem" direction="column">
            <Flex
              w="100%"
              bg={backgroundGrey}
              h="6px"
              borderRadius="1rem"
              position="relative"
              m="0 0 0.25rem"
            >
              <Flex
                position="absolute"
                top="0"
                left="0"
                bg={primaryAlt}
                h="6px"
                w={`${caseAcceptancePercentage}%`}
                borderRadius="1rem"
              />
            </Flex>

            <Flex align="center">
              <Text fontSize="0.8rem" color={textSecondary} fontWeight="500">
                {caseAcceptance.totalPatientsWithCompletedPlans || 0}{" "}
                {t("dashboard.accepted")}
              </Text>
              <Text
                fontSize="0.8rem"
                color={textSecondary}
                fontWeight="400"
                px="0.25rem"
              >
                |
              </Text>
              <Text fontSize="0.8rem" color={textSecondary} fontWeight="500">
                {caseAcceptance.totalPatientsWithPlans || 0}{" "}
                {t("dashboard.hasPlan")}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex> */}
    </Flex>
  );
};

export default DashboardCaseAcceptance;
